import useFileUpload from 'hooks/useFileUpload';
import React, { useEffect, useRef, useState } from 'react';
import styles from './FileSelect.module.css';
import { FaUpload } from 'react-icons/fa';
import SelectPrompt from './sub-components/SelectPrompt';
import SelectedFileControl from './sub-components/SelectedFileControl';
import { useSendRecording } from 'components/AudioRecorder/Recorder.api';
import { GetExtention, RemoveExtention } from 'utils/stringUtils';
import ClosableModal from 'components/ClosableModal/ClosableModal';
import FileOverridePrompt from 'components/FileOverridePrompt';

const MODES = {
  none: 0,
  'file-loaded': 1,
};

export default function FileSelect({
  file,
  fileTypes = ['*'],
  maxFileSize = 5000000,
  onChange = () => {},
  onSave = () => {},
}) {
  const { uploadProgress, clearProgress, sendRecording } = useSendRecording();

  const timeoutRef = useRef(null);

  const [mode, setMode] = useState(MODES['none']);
  const [errorMsg, setErrorMsg] = useState('');
  const [duplicateFile, setDuplicateFile] = useState(false);
  const [promptOverride, setPromptOverride] = useState(false);

  useEffect(() => {
    if (file) setMode(MODES['file-loaded']);
    else setMode(MODES['none']);
  }, [file]);

  useEffect(() => {
    if (uploadProgress !== null) setMode(MODES['file-loaded']);
    else setMode(MODES['none']);
  }, [uploadProgress]);

  const updateFile = async (e) => {
    setErrorMsg('');
    const newFile = e.target.files[0];

    if (!newFile) return;
    if (newFile.size > maxFileSize) {
      setErrorMsg('File too big!');
      return;
    }

    const ret = await sendRecording(
      newFile,
      RemoveExtention(newFile.name),
      GetExtention(newFile.name),
    );

    if (ret?.data?.expireTime)
      timeoutRef.current = setTimeout(() => {
        onChange(null);
      }, ret.data.expireTime);

    if (ret?.status === 200) {
      onChange(newFile);
      setDuplicateFile(ret?.data?.duplicateFile || false);
    }
  };

  const onFileDrop = async (e) => {
    setErrorMsg('');
    e.preventDefault();
    if (e.dataTransfer.files.length < 1) return;

    const arr = Array.from(e.dataTransfer.files).filter((file) =>
      fileTypes.some((ext) => ext === '*' || file.name.toLowerCase().endsWith(ext)),
    );

    const ret = await sendRecording(
      arr[0],
      RemoveExtention(arr[0].name),
      GetExtention(arr[0].name),
    );

    if (ret?.status === 200) {
      onChange(arr[0]);
      setDuplicateFile(ret?.data?.duplicateFile || false);
    }
  };

  const clearFile = () => {
    clearProgress();
    onChange(null);
  };

  const onSaveInt = () => {
    if (!duplicateFile) {
      onSaveResolve(false);
      return;
    }

    setPromptOverride(true);
  };

  const onSaveResolve = (override) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setPromptOverride(false);
    onSave(false, override);
    clearFile();
  };

  return (
    <div className="flex-fill h-100 d-flex flex-column justify-content-center align-items-center">
      {mode === MODES['none'] && (
        <SelectPrompt fileTypes={fileTypes} onUpdateFile={updateFile} onFileDrop={onFileDrop} />
      )}

      {mode === MODES['file-loaded'] && (
        <SelectedFileControl
          file={file}
          progress={uploadProgress}
          onClear={clearFile}
          onSave={onSaveInt}
        />
      )}

      <FileOverridePrompt
        fileName={file?.name}
        open={promptOverride}
        onClose={() => setPromptOverride(false)}
        onResolve={onSaveResolve}
      />

      {errorMsg && <p className="text-danger">{errorMsg}</p>}
    </div>
  );
}
